/**
 * Who may edit or delete a change order / upsell.
 *
 * These mirror `can_edit_change_order` / `delete_change_order` in
 * backend/routers/change_orders.py so a button only appears where the request
 * would actually succeed. Keep the two in step — a mismatch shows an action
 * that 403s, which reads as a broken app rather than a locked one.
 */

// Nothing has reached the client in these states — mirrors UNSENT_STATUSES.
export const UNSENT_STATUSES = [
  "draft", "ready_to_send", "pending_sales_review", "needs_pm_clarification",
];

// Mirrors FINANCE_ROLES. Sales is NOT finance here even though it carries
// can_view_financials — don't substitute a money visibility flag for this test,
// or the action shows up where the request 403s. (The backend also grants
// finance to three specific emails; those are not mirrored, so at worst an
// action hides where it would have worked.)
const FINANCE_ROLES = ["COO", "Admin", "Accountant"];

export const isFinanceRole = (role) => FINANCE_ROLES.includes(role);

// Money already recorded means the payment trail and the order total can fall
// out of step — the backend demands force=true from a finance user.
export const hasRecordedMoney = (co) =>
  !!(co?.deposit_paid || co?.balance_paid || co?.stripe_payment_status === "paid");

/** Did this user raise the row? Mirrors `_owns_change_order` — deliberately
 *  excludes pm_id: being assigned to someone else's request is not the same
 *  as having raised it. */
function ownsIt(co, user) {
  const email = (user?.email || "").toLowerCase();
  if (email && [co?.created_by_email, co?.sales_rep_email].some((e) => (e || "").toLowerCase() === email)) {
    return true;
  }
  return !!user?.id && user.id === co?.sales_rep_id;
}

/**
 * Finance edits anything at any stage; everyone else edits rows they raised
 * themselves, up until the moment it goes to the client.
 */
export function canEditAdditionalScope(co, { isFinance, user }) {
  if (!co) return false;
  if (isFinance) return true;
  return ownsIt(co, user) && UNSENT_STATUSES.includes(co.status);
}

/**
 * Same shape as editing: finance deletes anything, everyone else clears rows
 * they raised themselves before they go out.
 */
export function canDeleteAdditionalScope(co, { isFinance, user }) {
  if (!co) return false;
  if (isFinance) return true;
  return ownsIt(co, user) && UNSENT_STATUSES.includes(co.status);
}
